<template>
    <article
        class="bg-gray-50 rounded-lg shadow-sm min-h-[135.4px] grid grid-cols-[[base]_auto_[cards]_auto]"
        :aria-labelledby="headlineId"
    >
        <div class="col-[base]">
            <div class="flex flex-col h-full gap-2 p-2">
                <TextLink :id="headlineId" :to="linkTarget" class="font-bold">
                    {{ linkText }}
                </TextLink>
                <CardStatsList>
                    <CardStats :label="$t('execution-year')">
                        <span class="text-gray-700 font-semibold text-2xl/none">{{ executionYear }}</span>
                    </CardStats>
                    <CardStats :label="$t('allocation-variants')">
                        <span class="text-gray-700 font-semibold text-2xl/none">{{ variantsTotalCount }}</span>
                    </CardStats>
                    <CardStats :label="$t('event-status-label')">
                        <Badge size="Extra-Small" :variant="statusBadgeVariant">
                            {{ statusBadgeLabel }}
                        </Badge>
                    </CardStats>
                </CardStatsList>
            </div>
        </div>
        <div class="col-[cards] justify-self-end flex">
            <slot name="main-variant-tiles">
                <EventCardTiles
                    v-if="phaseTypes.length > 0 && variant"
                    :variant-id="variant.id"
                    :variant-index="variant.index"
                    :variant-status-badge-variant="variant.statusBadgeVariant"
                    :variant-status-badge-label="variant.statusBadgeLabel"
                    :variant-link-target="variant.linkTarget"
                    :phase-types="phaseTypes"
                    :link-to-reference-allocation="linkToReferenceAllocation"
                    :reference-allocation-query-document="referenceAllocationQueryDocument"
                    :reference-allocation-id="variant.referenceAllocationId"
                />
            </slot>
        </div>
    </article>
</template>

<script setup lang="ts">
import Badge, { BadgeVariant } from '@/components/Badge/Badge.vue';
import CardStats from '@/components/CardStats/CardStats.vue';
import CardStatsList from '@/components/CardStats/CardStatsList.vue';
import TextLink from '@/components/TextLink/TextLink.vue';
import { AllocationUsage } from '@/components/Timeline/allocation/types';
import EventCardTiles from '@/event/components/EventCardTiles.vue';
import { DocumentNode } from 'graphql';
import { nanoid } from 'nanoid';
import { RouteLocationRaw } from 'vue-router';

type EventCardProps = {
    linkTarget: RouteLocationRaw;
    linkText: string;
    statusBadgeVariant: BadgeVariant;
    statusBadgeLabel: string;
    variantsTotalCount: number;
    executionYear: number;
    variant?: {
        id: string;
        index: number;
        status: string;
        statusBadgeVariant: BadgeVariant;
        statusBadgeLabel: string;
        linkTarget: RouteLocationRaw;
        referenceAllocationId?: string;
    };
    phaseTypes: AllocationUsage[];
    linkToReferenceAllocation: RouteLocationRaw;
    referenceAllocationQueryDocument: DocumentNode;
    referenceAllocationLinkLabel: string;
};

defineProps<EventCardProps>();

const headlineId = nanoid();
</script>
